import React, { useState, useEffect } from 'react';
import { connectorService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import ConnectorTypeCard from './ConnectorTypeCard';
import MintsoftConnectionForm from './MintsoftConnectionForm';
import {
  Search,
  Filter,
  Plus,
  AlertTriangle,
  Lock,
  Info
} from 'lucide-react';

function ConnectorBrowser({ onConnectorAdded, onBack }) {
  const [connectorTypes, setConnectorTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [showMintsoftForm, setShowMintsoftForm] = useState(false);
  const { notify } = useNotification();

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'database', label: 'Databases' },
    { value: 'saas', label: 'SaaS Applications' },
    { value: 'marketing', label: 'Marketing & Analytics' },
  ];

  useEffect(() => {
    fetchConnectorTypes();
  }, []);

  const fetchConnectorTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await connectorService.getConnectorTypes();

      // Add Mintsoft as a hardcoded connector option (locked)
      const mintsoftConnector = {
        service: 'mintsoft',
        display_name: 'Mintsoft',
        description: 'Warehouse management and order fulfillment platform',
        category: 'saas',
        icon_url: null,
        is_custom: true,
        locked: true
      };

      // Lock all connectors - requires access key
      const lockedConnectors = (response || []).map(connector => ({
        ...connector,
        locked: true
      }));

      // Sort connectors: enabled ones first (alphabetically), then locked ones (alphabetically)
      const sortedConnectors = [mintsoftConnector, ...lockedConnectors].sort((a, b) => {
        // First sort by locked status
        if (a.locked !== b.locked) {
          return a.locked ? 1 : -1; // Unlocked (enabled) come first
        }
        // Then alphabetically by display name within each group
        return a.display_name.localeCompare(b.display_name);
      });

      setConnectorTypes(sortedConnectors);
    } catch (err) {
      console.error('Error fetching connector types:', err);
      setError('Failed to load connector types');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectorSelect = async (connector) => {
    try {
      setSelectedConnector(connector);

      // Handle Mintsoft specially (custom form)
      if (connector.service === 'mintsoft') {
        setShowMintsoftForm(true);
        return;
      }

      // Create Connect Card for Fivetran connectors
      const response = await connectorService.createConnectCard(
        connector.service,
        `${window.location.origin}/data-sources`
      );

      if (response.connect_card_url) {
        // Try opening in new window first (Fivetran's intended design)
        window.open(response.connect_card_url, '_blank', 'width=800,height=600');
        notify.info(`${connector.display_name} setup opened in new window. Complete setup and return here.`);
      } else {
        throw new Error('No Connect Card URL received');
      }
    } catch (err) {
      console.error('Error creating Connect Card:', err);
      notify.error(`Failed to setup ${connector.display_name}: ${err.message}`);
    }
  };

  const handleMintsoftSuccess = (data) => {
    setShowMintsoftForm(false);
    if (onConnectorAdded) onConnectorAdded(data);
  };


  const filteredConnectors = connectorTypes.filter(connector => {
    const matchesSearch = connector.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         connector.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || connector.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Show Mintsoft form if selected
  if (showMintsoftForm) {
    return (
      <MintsoftConnectionForm
        onSuccess={handleMintsoftSuccess}
        onBack={() => setShowMintsoftForm(false)}
      />
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary-600 dark:border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-slate-600 dark:text-slate-400">Loading connectors...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
          Failed to Load Connectors
        </h3>
        <p className="text-red-600 dark:text-red-500 mb-4">{error}</p>
        <button
          onClick={fetchConnectorTypes}
          className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white flex items-center">
            <Plus className="w-6 h-6 mr-2" />
            Add Data Connector
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Connect your data sources and automatically sync data to your account
          </p>
        </div>
      </div>

      {/* Access Required Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
              Access Key Required
            </h3>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Data connectors require an access key or special permission to use. Please contact support to enable connector access for your account.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search connectors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400"
          />
        </div>

        {/* Category Filter */}
        <div className="relative">
          <Filter className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="pl-10 pr-8 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 appearance-none"
          >
            {categories.map(category => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Connectors Grid */}
      {filteredConnectors.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredConnectors.map(connector => (
            <ConnectorTypeCard
              key={connector.service}
              connector={connector}
              onSelect={handleConnectorSelect}
              locked={connector.locked}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
            No connectors found
          </h3>
          <p className="text-slate-500 dark:text-slate-500">
            {searchTerm || selectedCategory
              ? 'Try adjusting your search or filter criteria'
              : 'No connectors available at the moment'
            }
          </p>
        </div>
      )}

    </div>
  );
}

export default ConnectorBrowser;